/**
 * DailyStatusDigestCard — "Today's Status" (Prompt 11E.8). Renders one
 * `DailyStatusDigestData`-shaped object — either a real per-tenant response
 * from `useDailyStatusDigest`, or (Owner, All mode) the client-side
 * consolidated sum of several tenants' responses (see
 * `mergeDailyStatusDigests` in OwnerDashboard.tsx). Never computes its own
 * numbers — purely a renderer + the "Copy as text" formatter.
 *
 * NIL-for-zero and Indian-grouped ₹ throughout, matching the digest's own
 * documented convention (docs/02-product/reports-specification.md).
 *
 * The exact WhatsApp-format wording below is a best-effort match of the
 * structure described in the prompt (header/month/date, Total Projections,
 * Total Received, Today's Collection, Renewals per cycle, Admissions,
 * New Enrolments, Drops) — it was not independently verifiable against a
 * real message transcript, so treat the literal copy as a reasonable
 * default, not a pixel-perfect reproduction.
 *
 * data-testid: candeza-dash-digest (or candeza-dash-digest-consolidated)
 */

import React from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, Button } from '@/components/common';
import { formatAmountOrNil, formatCountOrNil } from '@/api/dashboard';
import type { DailyStatusDigestData, BillingCycle } from '@/api/dashboard';

const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const TopFigures = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Figure = styled.div``;

const FigureValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const FigureLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CycleRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const CycleName = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: capitalize;
`;

const CardFooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const CYCLE_LABELS: Record<BillingCycle, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  yearly: 'Yearly',
  unknown: 'Unknown',
};

export function buildDigestCopyText(d: DailyStatusDigestData): string {
  const lines: string[] = [];
  lines.push(`${d.tenant_name} — Daily Status`);
  lines.push(`${d.month_label} · ${d.date}`);
  lines.push('');
  lines.push(`Total Projections: ${formatAmountOrNil(d.total_projections)}`);
  lines.push(`Total Received: ${formatAmountOrNil(d.total_received)}`);
  lines.push(`Today's Collection: ${formatAmountOrNil(d.todays_collection)}`);
  lines.push('');
  lines.push('Renewals:');
  for (const b of d.renewals_by_cycle) {
    if (b.projections_count === 0 && b.received_count === 0) continue;
    lines.push(
      `  ${CYCLE_LABELS[b.cycle]}: ${b.projections_count} x ${formatAmountOrNil(
        b.projections_count > 0 ? b.projections_amount / b.projections_count : 0,
      )} = ${formatAmountOrNil(b.projections_amount)} | Received: ${formatAmountOrNil(b.received_amount)} (${b.received_count})`,
    );
  }
  lines.push('');
  lines.push(`Admissions this month: ${formatCountOrNil(d.total_admissions_this_month)}`);
  if (d.todays_admissions_by_subject.length > 0) {
    for (const s of d.todays_admissions_by_subject) {
      lines.push(`  ${s.subject_name}: ${s.count}`);
    }
  }
  lines.push(`Demos today: ${formatCountOrNil(d.demos_today)}`);
  lines.push('');
  lines.push('New Enrolments:');
  for (const b of d.new_enrolments_by_cycle) {
    if (b.count === 0) continue;
    lines.push(`  ${CYCLE_LABELS[b.cycle]}: ${b.count} = ${formatAmountOrNil(b.amount)}`);
  }
  lines.push('');
  lines.push('Drops/Suspended:');
  const anyDrops = d.drops_by_cycle.some((b) => b.count > 0);
  if (!anyDrops) {
    lines.push('  NIL');
  } else {
    for (const b of d.drops_by_cycle) {
      if (b.count === 0) continue;
      lines.push(`  ${CYCLE_LABELS[b.cycle]}: ${b.count}`);
    }
  }
  return lines.join('\n');
}

export interface DailyStatusDigestCardProps {
  data: DailyStatusDigestData;
  title?: string;
  testId?: string;
}

export const DailyStatusDigestCard: React.FC<DailyStatusDigestCardProps> = ({
  data,
  title,
  testId = 'candeza-dash-digest',
}) => {
  const copyAsText = async () => {
    const text = buildDigestCopyText(data);
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — clipboard access denied');
    }
  };

  return (
    <Card padding="lg" data-testid={testId}>
      <CardHeader>
        <CardTitle>{title ?? `${data.tenant_name} — Today's Status`}</CardTitle>
      </CardHeader>

      <TopFigures>
        <Figure>
          <FigureValue>{formatAmountOrNil(data.total_projections)}</FigureValue>
          <FigureLabel>Total Projections</FigureLabel>
        </Figure>
        <Figure>
          <FigureValue>{formatAmountOrNil(data.total_received)}</FigureValue>
          <FigureLabel>Total Received</FigureLabel>
        </Figure>
        <Figure>
          <FigureValue>{formatAmountOrNil(data.todays_collection)}</FigureValue>
          <FigureLabel>Today's Collection</FigureLabel>
        </Figure>
      </TopFigures>

      <Section>
        <SectionTitle>Renewals by Cycle</SectionTitle>
        {data.renewals_by_cycle
          .filter((b) => b.projections_count > 0 || b.received_count > 0)
          .map((b) => (
            <CycleRow key={b.cycle}>
              <CycleName>{CYCLE_LABELS[b.cycle]}</CycleName>
              <span>
                {b.projections_count} due · {formatAmountOrNil(b.projections_amount)} proj. ·{' '}
                {formatAmountOrNil(b.received_amount)} recv. ({b.received_count})
              </span>
            </CycleRow>
          ))}
        {data.renewals_by_cycle.every((b) => b.projections_count === 0 && b.received_count === 0) && (
          <CycleRow>
            <span>NIL</span>
          </CycleRow>
        )}
      </Section>

      <Section>
        <SectionTitle>Admissions &amp; Demos</SectionTitle>
        <CycleRow>
          <CycleName>This month</CycleName>
          <span>{formatCountOrNil(data.total_admissions_this_month)}</span>
        </CycleRow>
        <CycleRow>
          <CycleName>Demos today</CycleName>
          <span>{formatCountOrNil(data.demos_today)}</span>
        </CycleRow>
      </Section>

      <Section>
        <SectionTitle>New Enrolments</SectionTitle>
        {data.new_enrolments_by_cycle
          .filter((b) => b.count > 0)
          .map((b) => (
            <CycleRow key={b.cycle}>
              <CycleName>{CYCLE_LABELS[b.cycle]}</CycleName>
              <span>{b.count} · {formatAmountOrNil(b.amount)}</span>
            </CycleRow>
          ))}
        {data.new_enrolments_by_cycle.every((b) => b.count === 0) && (
          <CycleRow>
            <span>NIL</span>
          </CycleRow>
        )}
      </Section>

      <Section>
        <SectionTitle>Drops / Suspended</SectionTitle>
        {data.drops_by_cycle
          .filter((b) => b.count > 0)
          .map((b) => (
            <CycleRow key={b.cycle}>
              <CycleName>{CYCLE_LABELS[b.cycle]}</CycleName>
              <span>{b.count}</span>
            </CycleRow>
          ))}
        {data.drops_by_cycle.every((b) => b.count === 0) && (
          <CycleRow>
            <span>NIL</span>
          </CycleRow>
        )}
      </Section>

      <CardFooterRow>
        <Button variant="secondary" size="sm" onClick={copyAsText} data-testid={`${testId}-copy-btn`}>
          Copy as text
        </Button>
      </CardFooterRow>
    </Card>
  );
};
